// import React from 'react'

// function SelectBox({
//   options = [],
//   value = '',
//   selectionMessage,
//   handleChange,
//   ...props
// }) {
//   return (
//     <>
//       {props.label && (
//         <label className="block mb-2 text-sm font-medium text-gray-700">
//           {props.label}{' '}
//           {props.required && <span className="text-red-500">*</span>}
//         </label>
//       )}
//       <select
//         className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm
//                    focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
//                    disabled:bg-gray-100 disabled:cursor-not-allowed"
//         onChange={(e) => handleChange(e)}
//         value={value}
//         name={props.name}
//         disabled={props.disabled}
//       >
//         <option value="">{selectionMessage ?? 'Select a value'}</option>
//         {options.map((place, i) => (
//           <option value={place.name} key={i}>
//             {place.name}
//           </option>
//         ))}
//       </select>
//     </>
//   )
// }

// export default SelectBox

import React, { useState, useRef, useEffect } from 'react'

function SelectBox({
  options = [],
  value = '',
  selectionMessage,
  handleChange,
  ...props
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedValue, setSelectedValue] = useState(value)
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  })
  const dropdownRef = useRef(null)
  const buttonRef = useRef(null)
  const headerHeight = 64 // Adjust this to match your actual header height

  // Sync selectedValue with value prop
  useEffect(() => {
    setSelectedValue(value)
  }, [value])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Update dropdown position when opened or on scroll
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const updatePosition = () => {
        const rect = buttonRef.current.getBoundingClientRect()

        // Check if button is completely hidden or too close to header
        if (rect.bottom < headerHeight || rect.top < 0) {
          setIsOpen(false)
          return
        }

        // Calculate the actual top position (ensure it doesn't go above header)
        const dropdownTop = Math.max(rect.bottom, headerHeight)

        setDropdownPosition({
          top: dropdownTop,
          left: rect.left,
          width: rect.width,
        })
      }

      updatePosition()
      window.addEventListener('scroll', updatePosition, true)
      window.addEventListener('resize', updatePosition)

      return () => {
        window.removeEventListener('scroll', updatePosition, true)
        window.removeEventListener('resize', updatePosition)
      }
    }
  }, [isOpen])

  const handleSelect = (option) => {
    setSelectedValue(option.name)
    setIsOpen(false)
    handleChange({ target: { value: option.name, name: props.name } })
  }

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {props.label && (
        <label className="block mb-2 text-sm font-medium text-gray-700">
          {props.label}
          {props.required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div
        ref={buttonRef}
        className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm 
                 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 
                 disabled:bg-gray-100 disabled:cursor-not-allowed flex justify-between items-center cursor-pointer"
        onClick={() => !props.disabled && setIsOpen(!isOpen)}
      >
        <span>{selectedValue || (selectionMessage ?? 'Select a value')}</span>
        <svg
          className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
            isOpen ? 'transform rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      {isOpen && (
        <div
          className="fixed z-[5] bg-white border border-gray-300 rounded-md shadow-lg max-h-68 overflow-auto"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            maxHeight: `calc(100vh - ${dropdownPosition.top}px - 20px)`,
          }}
        >
          {options.map((option, index) => (
            <div
              key={index}
              className={`px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer ${
                selectedValue === option.name ? 'bg-blue-50' : ''
              }`}
              onClick={() => handleSelect(option)}
            >
              {option.name}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default SelectBox
